import type { Dictionary } from '@reduxjs/toolkit';
import { parse, ParsedQuery, stringify } from 'query-string';
import { Filter } from 'components/molecules/filters/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import {
  GroupSeparator,
  KeySeparator,
  TermSeparator,
} from 'components/organisms/tasks/filters/separators';
import {
  GroupCriteria,
  GroupedFilter,
} from 'components/organisms/tasks/grouped-filter-popover';
import { defaultGroupedFilter } from 'components/organisms/tasks/utils';
import { Transformer as SearchTransformer } from 'hooks/use-search';
import { logger } from 'services';
import { QueryCombinerType } from '../organisms/tasks/combiner';
import { FilterableField } from '../organisms/tasks/fields';
import { FieldFilter } from '../organisms/tasks/filters';
import { FilterGroupType } from './types';

// Transformer V1
type FieldLookup = Dictionary<FilterableField>;

export type ParsedFilterQuery = ParsedQuery & {
  type: QueryCombinerType;
  criteria: string[];
};

const tryParse = (value: string, lookup: FieldLookup): FieldFilter | null => {
  try {
    const [fieldName, ...rest] = value.split(TermSeparator);
    const field = lookup[fieldName];
    const filter = rest.join(TermSeparator);

    return field ? FieldFilter.parse(field, filter) : null;
  } catch (error) {
    logger.error('failed to parse criteria', { value, error: error as Error });

    return null;
  }
};

export const to_parsed_filter_query = ({
  type,
  criteria,
}: ParsedQuery): Partial<ParsedFilterQuery> => {
  const result = {
    type: (type as QueryCombinerType) ?? QueryCombinerType.All,
    criteria: (typeof criteria === 'string'
      ? [criteria]
      : criteria ?? []) as string[],
  };

  return {
    ...result,
  };
};

export const from_filter_query =
  (lookup: FieldLookup) =>
  (query: Partial<ParsedFilterQuery>): Filter =>
    Object.keys(lookup).length === 0
      ? defaultFilter
      : {
          type: query.type ?? QueryCombinerType.All,
          criteria: (query.criteria ?? [])
            .map((x) => tryParse(x, lookup))
            .filter((x) => x != null)
            .map((x) => x as FieldFilter),
        };

export const to_filter_query = (filter: Filter): ParsedFilterQuery => {
  return {
    criteria: filter.criteria
      .filter(FieldFilter.isValid)
      .map(FieldFilter.toString),
    type: filter.type,
  };
};

export const from_search =
  (lookup: FieldLookup) =>
  (filter: string): Filter =>
    from_filter_query(lookup)(to_parsed_filter_query(parse(filter)));

export const to_query = (filter: Filter): string =>
  `?${stringify(to_filter_query(filter))}`;

export const getFilterTransformer = (
  lookup: FieldLookup,
): SearchTransformer<Filter> => ({
  from: (parsed) => from_filter_query(lookup)(to_parsed_filter_query(parsed)),
  to: to_filter_query,
});

// Transformer V2
export interface GroupFieldMeta {
  id: FilterGroupType;
  fields: FieldLookup;
}

export type GroupFieldsMeta = GroupFieldMeta[];

export const getFilterGroupType = (
  value: string,
  defaultType?: FilterGroupType,
): FilterGroupType => {
  if (
    Object.values(FilterGroupType).includes(
      value?.toLowerCase() as unknown as FilterGroupType,
    )
  ) {
    return value as unknown as FilterGroupType;
  }

  return defaultType ?? FilterGroupType.Task;
};

const lookUpForGroup = (
  group: FilterGroupType,
  groupFieldsMeta: GroupFieldsMeta,
  fieldName: string,
) => groupFieldsMeta.find(({ id }) => id === group)?.fields[fieldName];

const tryParseGroups = (
  filterString: string,
  groupFieldsMeta: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
): GroupCriteria | null => {
  try {
    const splitByGroups = filterString.split(GroupSeparator);

    if (splitByGroups.length === 2) {
      const [groupName, criterion] = splitByGroups;
      const [fieldName, ...rest] = criterion.split(TermSeparator);

      const [groupNameWithoutKey, key] = groupName.split(KeySeparator);

      const field = lookUpForGroup(
        getFilterGroupType(groupNameWithoutKey),
        groupFieldsMeta,
        fieldName,
      );
      const filter = rest.join(TermSeparator);

      if (!field) {
        return null;
      }

      return {
        id: getFilterGroupType(groupNameWithoutKey),
        criteria: FieldFilter.parse(field, filter),
        ...(key ? { key } : {}),
      };
    }

    const [fieldName, ...rest] = filterString.split(TermSeparator);
    const field = lookUpForGroup(defaultGroup, groupFieldsMeta, fieldName);
    const filter = rest.join(TermSeparator);

    if (!field) {
      return null;
    }

    return {
      id: getFilterGroupType(field.sourceType),
      criteria: FieldFilter.parse(field, filter),
    };
  } catch (err) {
    logger.error('failed to parse criteria', {
      filterString,
      error: err as Error,
    });

    return null;
  }
};

export const to_parsed_filter_query_for_groups = ({
  type,
  criteria,
}: ParsedQuery): Partial<ParsedFilterQuery> => ({
  type: (type as QueryCombinerType) ?? QueryCombinerType.All,
  criteria: (typeof criteria === 'string'
    ? [criteria]
    : criteria ?? []) as string[],
});

export const from_filter_query_for_groups =
  (groupFieldsMeta: GroupFieldsMeta, defaultGroup: FilterGroupType) =>
  (query: Partial<ParsedFilterQuery>): GroupedFilter =>
    groupFieldsMeta.every((lookup) => Object.keys(lookup).length === 0)
      ? defaultGroupedFilter(defaultGroup)
      : {
          type: query.type ?? QueryCombinerType.All,
          defaultGroup,
          groupCriteria: (query.criteria ?? [])
            .map((x) => tryParseGroups(x, groupFieldsMeta, defaultGroup))
            .filter((x) => x != null)
            .map((x) => x as GroupCriteria),
        };

export const to_filter_query_for_groups = (
  filter: GroupedFilter,
): ParsedFilterQuery => ({
  type: filter.type,
  criteria: filter.groupCriteria
    .filter(({ criteria }) => FieldFilter.isValid(criteria))
    .map(
      ({ criteria, key }) =>
        `${criteria.field.sourceType.toLowerCase()}${
          key ? `${KeySeparator}${key}` : ''
        }${GroupSeparator}${FieldFilter.toString(criteria as FieldFilter)}`,
    ),
});

export const getGroupFilterTransformer = (
  groupFieldsMeta: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
): SearchTransformer<GroupedFilter> => ({
  from: (parsed) =>
    from_filter_query_for_groups(
      groupFieldsMeta,
      defaultGroup,
    )(to_parsed_filter_query_for_groups(parsed)),
  to: to_filter_query_for_groups,
});

export const to_query_for_groups = (filter: GroupedFilter): string =>
  `?${stringify(to_filter_query_for_groups(filter))}`;
